import {
  ArrowLeftIcon,
  CalendarIcon,
  ClockIcon,
  PencilSquareIcon,
  UserGroupIcon,
} from '@heroicons/react/24/solid';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Input as HeroInput,
  Textarea as HeroTextarea,
} from '@heroui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import {
  Controller,
  type FieldArrayWithId,
  FormProvider,
  type SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import MeetingLocationSelector from '@/app/components/account/meeting-location-selector.tsx';
import EventTimeAndLocation from '@/app/components/calendar/event-time-and-location.tsx';
import RepresentativeAccess from '@/app/components/representative-access.tsx';
import StaffAvailabilityList from '@/app/components/staff-availability-list.tsx';
import toast from '@/app/toast.ts';
import type { AccountMeetingLocationModel } from '@/app/types/account.type.ts';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@/primitives/dialog.tsx';
import { Heading, Subheading } from '@/primitives/heading.tsx';
import {
  useFetchAvailabilitySlotQuery,
  usePutAvailabilitySlotMutation,
} from '@/slices/availability-slot-api-slice.ts';
import { useFetchStaffMembersQuery } from '@/slices/staff-api-slice.ts';
import { useSecurityContext } from '../../../hooks/security-provider.tsx';
import type { AccountCategoryModel } from '../../../types/account-category.type.ts';
import type { AccountRepresentativeListModel } from '../../../types/account-representative.type.ts';
import type {
  AvailabilitySlotListModel,
  AvailabilitySlotPutModel,
  StaffMemberAvailabilityModel,
} from '../../../types/availability-slot.type.ts';
import type { ConfirmationStatusEnum } from '../../../types/confirmation-status.model.ts';
import { getDateFormat, getTimeFormat } from '../../../types/constants.ts';
import { sortStaffMembers } from './helpers-availability.ts';

export interface EditAvailabilitySlotForm {
  date: string;
  startTime: string;
  endTime: string;
  officeNotes: string;
  accountMeetingLocation: AccountMeetingLocationModel;
  accountRepresentativeIds: number[];
  accountCategoryIds: number[];
  staffMemberAvailabilities: StaffMemberAvailabilityModel[];
}

const EditAvailability = () => {
  const params = useParams();
  const { currentAccount, setCurrentAccount, visibleAccounts } = useSecurityContext();
  const { data, isLoading } = useFetchAvailabilitySlotQuery(params?.key ?? skipToken);
  const [put, { isLoading: putLoading }] = usePutAvailabilitySlotMutation();
  const { data: accountStaffMembers, isLoading: staffMembersLoading } = useFetchStaffMembersQuery(
    data?.accountKey ? `accountKey=${data?.accountKey}` : skipToken,
  );
  const [conflictingAvailabilitySlot, setConflictingAvailabilitySlot] =
    useState<AvailabilitySlotListModel | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (data?.accountKey && data.accountKey !== currentAccount?.key) {
      const newAccount = visibleAccounts?.find(x => x.key === data?.accountKey);
      setCurrentAccount(newAccount);
    }
  }, [data, currentAccount, visibleAccounts]);

  const form = useForm<EditAvailabilitySlotForm>({
    values: {
      date: DateTime.fromISO(data?.start).toISODate(),
      startTime: getTimeFormat(data?.start),
      endTime: getTimeFormat(data?.end),
      officeNotes: data?.officeNotes || '',
      accountMeetingLocation: data?.accountMeetingLocation,
      accountRepresentativeIds: data?.accountRepresentatives.map(x => x.id) || [],
      accountCategoryIds: data?.accountCategories.map(x => x.id) || [],
      staffMemberAvailabilities: data?.staffMemberAvailabilities || [],
    },
    criteriaMode: 'all',
    resetOptions: {
      keepErrors: false,
    },
  });

  const { control, handleSubmit } = form;

  const onSubmit: SubmitHandler<EditAvailabilitySlotForm> = async (
    value: EditAvailabilitySlotForm,
  ) => {
    const start = new Date(
      `${getDateFormat(value.date)} ${getTimeFormat(value.startTime)}`,
    ).toISOString();
    const end = new Date(
      `${getDateFormat(value.date)} ${getTimeFormat(value.endTime)}`,
    ).toISOString();

    const staffMemberAvailabilities = value.staffMemberAvailabilities.reduce<{
      [staffMemberId: number]: ConfirmationStatusEnum;
    }>((map, staffMemberAvailability) => {
      map[staffMemberAvailability.staffMember.id] =
        staffMemberAvailability.confirmationStatus?.confirmationStatusId;
      return map;
    }, {});

    const model: AvailabilitySlotPutModel = {
      start,
      end,
      officeNotes: value.officeNotes,
      accountMeetingLocationId: value.accountMeetingLocation.id,
      accountRepresentativeIds: value.accountRepresentativeIds,
      accountCategoryIds: value.accountCategoryIds,
      staffMemberAvailabilities: staffMemberAvailabilities,
    };

    try {
      await put({ key: data.key, model }).unwrap();
      toast.success('Availability Saved!');
      navigate('/calendar');
    } catch (error) {
      if (error.status === 409) {
        setConflictingAvailabilitySlot(error.data.conflictingAvailabilitySlot);
      } else {
        toast.error('Error saving availability slot. Please try again.');
      }
    }
  };

  const {
    fields: staffMemberFields,
    append: appendStaffMember,
    update: updateStaffMember,
    replace: replaceStaffMembers,
  } = useFieldArray({
    control: control,
    name: 'staffMemberAvailabilities',
  });

  // add staff members on initial load
  useEffect(() => {
    if (accountStaffMembers?.length) {
      replaceStaffMembers([]);

      const staffMembers = sortStaffMembers(
        accountStaffMembers.filter(x =>
          x.accountSiteStaffMembers.some(y => y.accountId === data?.accountId),
        ),
      );

      for (const staffMember of staffMembers) {
        const staffMemberAvailability = data?.staffMemberAvailabilities?.find(
          x => x.staffMember.id === staffMember.id,
        );

        appendStaffMember({
          staffMember,
          availabilitySlotId: data?.id,
          confirmationStatus: staffMemberAvailability?.confirmationStatus,
        });
      }
    }
  }, [accountStaffMembers]);

  const setConfirmationStatus = (
    value: FieldArrayWithId<EditAvailabilitySlotForm, 'staffMemberAvailabilities', 'id'>,
    status: ConfirmationStatusEnum,
    index: number,
  ) => {
    if (value.confirmationStatus?.confirmationStatusId === status) {
      return;
    }

    value.confirmationStatus = {
      confirmationStatusId: status,
      description: '',
    };
    updateStaffMember(index, value);
  };

  const handleRepsChange = (
    accountRepresentatives: AccountRepresentativeListModel[],
    accountCategories: AccountCategoryModel[],
  ) => {
    form.setValue(
      'accountRepresentativeIds',
      accountRepresentatives.map(x => x.id),
    );
    form.setValue(
      'accountCategoryIds',
      accountCategories.map(x => x.id),
    );
  };

  return (
    <>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='flex flex-col gap-6 self-center mx-auto w-full max-w-screen-xl'>
            <Button
              color='default'
              variant='light'
              onPress={() => navigate(-1)}
              className='self-start'
            >
              <ArrowLeftIcon className='size-5' />
              Back
            </Button>

            <Heading icon={<PencilSquareIcon className='size-7' />}>
              Edit Event & Availability
            </Heading>

            <div className='flex flex-col gap-6'>
              <Card shadow='sm' className='border border-gray-200'>
                <CardHeader className='flex gap-3 bg-gradient-to-br from-primary-50 to-primary-100'>
                  <ClockIcon className='size-6 text-primary-600' />
                  <Subheading>Basic Details</Subheading>
                </CardHeader>
                <Divider />
                <CardBody className='p-6'>
                  <div className='flex flex-col gap-4'>
                    <Controller
                      control={control}
                      name='date'
                      rules={{ required: 'This field is required' }}
                      render={({ field, fieldState }) => (
                        <HeroInput
                          {...field}
                          type='date'
                          label='Date'
                          variant='bordered'
                          isRequired
                          isInvalid={fieldState.invalid}
                          errorMessage={fieldState.error?.message}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name='startTime'
                      rules={{ required: 'This field is required' }}
                      render={({ field, fieldState }) => (
                        <HeroInput
                          {...field}
                          type='time'
                          label='Start Time'
                          variant='bordered'
                          isRequired
                          isInvalid={fieldState.invalid}
                          errorMessage={fieldState.error?.message}
                          startContent={<ClockIcon className='size-4 text-default-400' />}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name='endTime'
                      rules={{ required: 'This field is required' }}
                      render={({ field, fieldState }) => (
                        <HeroInput
                          {...field}
                          type='time'
                          label='End Time'
                          variant='bordered'
                          isRequired
                          isInvalid={fieldState.invalid}
                          errorMessage={fieldState.error?.message}
                          startContent={<ClockIcon className='size-4 text-default-400' />}
                        />
                      )}
                    />
                    <MeetingLocationSelector name={'accountMeetingLocation'} />
                    <Controller
                      control={control}
                      name='officeNotes'
                      render={({ field, fieldState }) => (
                        <HeroTextarea
                          {...field}
                          label='Instructions for Reps'
                          placeholder='Provide instructions for the representative (parking, building access, special requirements, etc.)'
                          variant='bordered'
                          isInvalid={fieldState.invalid}
                          errorMessage={fieldState.error?.message}
                          minRows={3}
                        />
                      )}
                    />
                  </div>
                </CardBody>
              </Card>

              <Card shadow='sm' className='border border-gray-200'>
                <CardHeader className='flex gap-3 bg-gradient-to-br from-primary-50 to-primary-100'>
                  <CalendarIcon className='size-6 text-primary-600' />
                  <Subheading>Representative Access</Subheading>
                </CardHeader>
                <Divider />
                <CardBody className='p-6'>
                  <RepresentativeAccess
                    existingAccountRepresentatives={data?.accountRepresentatives}
                    existingCategories={data?.accountCategories}
                    onChange={handleRepsChange}
                  />
                </CardBody>
              </Card>

              <Card shadow='sm' className='border border-gray-200'>
                <CardHeader className='flex gap-3 bg-gradient-to-br from-primary-50 to-primary-100'>
                  <UserGroupIcon className='size-6 text-primary-600' />
                  <Subheading>Staff Availability</Subheading>
                </CardHeader>
                <Divider />
                <CardBody className='p-6'>
                  <StaffAvailabilityList
                    staffMemberAvailabilities={staffMemberFields}
                    accountId={data?.accountId}
                    isEditable={true}
                    onStatusChange={(_staffMemberId, status, index) => {
                      if (index !== undefined) {
                        const staff = staffMemberFields[index];
                        setConfirmationStatus(staff, status, index);
                      }
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div className='flex pt-8'>
              <Button
                color='primary'
                type='submit'
                isLoading={isLoading || putLoading || staffMembersLoading}
                size='lg'
              >
                Save Changes
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
      <Dialog
        open={!!conflictingAvailabilitySlot}
        onClose={() => setConflictingAvailabilitySlot(null)}
      >
        <DialogTitle>Conflict</DialogTitle>
        <DialogBody>
          <p>
            This availability slot cannot be edited because the updated time conflicts with an
            existing availability slot:
          </p>

          {conflictingAvailabilitySlot && (
            <div className='mt-4'>
              <EventTimeAndLocation
                start={conflictingAvailabilitySlot.start}
                end={conflictingAvailabilitySlot.end}
                location={conflictingAvailabilitySlot.accountMeetingLocation.description}
              />
            </div>
          )}
        </DialogBody>
        <DialogActions>
          <Button
            color='default'
            variant='bordered'
            onPress={() => setConflictingAvailabilitySlot(null)}
          >
            Okay
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditAvailability;
